// BrandsMobile.jsx — onglet Marques (mobile). CRUD complet.
// Branché sur BrandsService (identique au desktop Brands.jsx).
import React, { useMemo, useState } from "react";
import { message, Modal } from "antd";
import BrandsService from "../../gestion-brands/BrandsService";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow,
  Field, TextInput, TextAreaInput, Mono, Pill, CountChip, Ico,
  useResourceList, useMobileMenu, genSlug, fmt,
} from "./productMobileKit";

const PERM = {
  create: "Gestion Commerciale.Produits.Marques.create",
  edit: "Gestion Commerciale.Produits.Marques.edit",
  delete: "Gestion Commerciale.Produits.Marques.delete",
};
const countOf = (b) => {
  const n = b.products_count ?? b.product_count ?? b.products ?? b.nb_produits;
  return Number.isFinite(Number(n)) ? Number(n) : null;
};
const statusOf = (b) => (b.status ?? b.statut ?? null);
const isActive = (b) => ["active", "actif", "1", "true"].includes(String(statusOf(b)).toLowerCase());
const isInactive = (b) => ["inactive", "inactif", "0", "false"].includes(String(statusOf(b)).toLowerCase());
const statusPill = (b) => {
  const s = statusOf(b);
  if (s == null) return null;
  if (isActive(b)) return { tone: "ok", label: "Active" };
  if (isInactive(b)) return { tone: "off", label: "Inactive" };
  return { tone: "draft", label: String(s) };
};

export default function BrandsMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const { hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const can = { create: hasPermission(PERM.create), edit: hasPermission(PERM.edit), delete: hasPermission(PERM.delete) };

  const { rows: brands, loading, reload } = useResourceList(
    async () => (selectedCompany ? (await BrandsService.getAll(selectedCompany)).data : []),
    [selectedCompany]
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sheet, setSheet] = useState(null);            // {mode:'form'|'actions', brand}
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [saving, setSaving] = useState(false);

  const hasStatus = useMemo(() => brands.some((b) => statusOf(b) != null), [brands]);
  const hasCounts = useMemo(() => brands.some((b) => countOf(b) != null), [brands]);
  const totalProducts = useMemo(() => brands.reduce((s, b) => s + (countOf(b) || 0), 0), [brands]);
  const activeCount = useMemo(() => brands.filter(isActive).length, [brands]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return brands.filter((b) => {
      if (filter === "active" && !isActive(b)) return false;
      if (filter === "inactive" && !isInactive(b)) return false;
      if (q && ![b.name, b.slug, b.description].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [brands, query, filter]);

  const openCreate = () => {
    if (!selectedCompany) return message.warning("Veuillez sélectionner une entreprise");
    setForm({ name: "", slug: "", description: "" });
    setSheet({ mode: "form", brand: null });
  };
  const openEdit = (b) => { setForm({ name: b.name || "", slug: b.slug || "", description: b.description || "" }); setSheet({ mode: "form", brand: b }); };
  const openActions = (b) => {
    if (can.edit && !can.delete) return openEdit(b);
    if (!can.edit && !can.delete) return;
    setSheet({ mode: "actions", brand: b });
  };
  const close = () => { if (!saving) setSheet(null); };
  const onName = (v) => setForm((f) => ({ ...f, name: v, slug: genSlug(v) }));

  const submit = async () => {
    if (!form.name.trim()) return message.warning("Veuillez saisir le nom de la marque");
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), slug: form.slug || genSlug(form.name), description: form.description || "", company_id: selectedCompany };
      if (sheet.brand) { await BrandsService.update(sheet.brand.id, payload); message.success("Marque mise à jour avec succès"); }
      else { await BrandsService.create(payload); message.success("Marque créée avec succès"); }
      setSheet(null); reload();
    } catch (err) {
      const st = err.response?.status;
      if (st === 409) message.error(err.response?.data?.error || "Cette marque existe déjà !");
      else if (st === 400) message.error(err.response?.data?.error || "Données invalides");
      else message.error("Erreur lors de la sauvegarde de la marque");
    } finally { setSaving(false); }
  };

  const askDelete = (b) => {
    setSheet(null);
    Modal.confirm({
      title: "Supprimer cette marque ?", content: "Cette action pourrait affecter les produits associés.",
      okText: "Oui, Supprimer", okType: "danger", cancelText: "Non", centered: true,
      onOk: async () => {
        try { await BrandsService.delete(b.id); message.success("Marque supprimée avec succès"); reload(); }
        catch (err) {
          if (err.response?.status === 409 && err.response?.data?.error) message.error(err.response.data.error);
          else message.error("Erreur lors de la suppression de la marque.");
        }
      },
    });
  };

  return (
    <ProductScreen>
      <Appbar
        title="Marques" onMenu={onMenu}
        subtitle={`${brands.length} marque${brands.length > 1 ? "s" : ""}${hasCounts ? ` · ${fmt(totalProducts)} produits` : ""}`}
        action={<button className="iconbtn"><Ico name="sliders-horizontal" /></button>}
      />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Marques", value: brands.length },
          hasStatus && { label: "Actives", value: activeCount },
          hasCounts && { label: "Produits liés", value: fmt(totalProducts) },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher une marque…" />
        {hasStatus ? (
          <Chips value={filter} onChange={setFilter} options={[
            { key: "all", label: "Toutes" }, { key: "active", label: "Actives" }, { key: "inactive", label: "Inactives" },
          ]} />
        ) : null}
      </div>

      <div className="body">
        {loading || !visible.length ? (
          <EmptyState
            icon="tag" loading={loading}
            title={loading ? "Chargement des marques" : (query || filter !== "all" ? "Aucun résultat" : "Aucune marque pour l’instant")}
            text={loading ? "Synchronisation en cours." : (query || filter !== "all"
              ? "Aucune marque ne correspond à votre recherche."
              : "Créez votre première marque pour organiser vos produits.")}
          />
        ) : (
          <div className="list">
            {visible.map((b) => (
              <button key={b.id} className="rcard" onClick={() => openActions(b)}>
                <Mono name={b.name} />
                <div className="rcard__main">
                  <div className="rcard__name">{b.name}</div>
                  <div className="rcard__meta">
                    {b.slug ? <span className="rcard__sub">/{b.slug}</span> : null}
                    <CountChip value={countOf(b)} />
                    <Pill {...(statusPill(b) || {})} />
                  </div>
                </div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Nouvelle" /> : null}

      <Sheet open={sheet?.mode === "form"} onClose={close}
        title={sheet?.brand ? "Modifier la marque" : "Nouvelle marque"}
        subtitle="Elle sera disponible pour tous vos produits.">
        <div className="preview">
          <Mono name={form.name || "?"} />
          <div>
            <div className="preview__name">{form.name.trim() || "Nom de la marque"}</div>
            <div className="preview__slug">/m/<b>{form.slug || "slug-auto"}</b></div>
          </div>
        </div>
        <Field label="Nom de la marque">
          <TextInput value={form.name} onChange={onName} placeholder="Ex : SuperMarque" icon="tag" autoFocus />
        </Field>
        <Field label="Slug" hint="(auto-généré)">
          <TextInput value={form.slug} readOnly placeholder="sera-genere-automatiquement" />
        </Field>
        <Field label="Description" hint="facultatif">
          <TextAreaInput value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="Brève description…" />
        </Field>
        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={close} disabled={saving}>Annuler</button>
          <button className="btn btn--primary" onClick={submit} disabled={saving || !form.name.trim()}>
            <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
            {sheet?.brand ? "Mettre à jour" : "Créer la marque"}
          </button>
        </div>
      </Sheet>

      <Sheet open={sheet?.mode === "actions"} onClose={close}>
        {sheet?.brand ? (
          <>
            <div className="preview">
              <Mono name={sheet.brand.name} />
              <div>
                <div className="preview__name">{sheet.brand.name}</div>
                {sheet.brand.slug ? <div className="preview__slug">/{sheet.brand.slug}</div> : null}
              </div>
            </div>
            {can.edit ? <ActionRow icon="pencil" label="Modifier la marque" onClick={() => openEdit(sheet.brand)} /> : null}
            {can.delete ? <ActionRow icon="trash-2" label="Supprimer la marque" danger onClick={() => askDelete(sheet.brand)} /> : null}
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
